import { curry } from 'lodash';
import { isFunction } from 'lodash/fp';
import { QueryClient, QueryKey } from 'react-query';

type Success<T> = { status: 'success'; value: T };
type Failure = { status: 'failure'; reason: Error };

export type Settled<T> = Success<T> | Failure;

const settle = <T>(promise: Promise<T>): Promise<Settled<T>> => promise
  .then(value => ({ status: 'success', value } as Success<T>))
  .catch(err => ({ status: 'failure', reason: err } as Failure));

export const allSettled = <T>(promises: Promise<T>[]): Promise<Settled<T>[]> => Promise.all(promises.map(settle));


export function push<T>(a: T) {
  return (b: T[]): T[] => [...b, a];
}

/*
 * Setting them value to undefined and the Max-Age to a value below zero will
 * essentially 'delete' the cookie
 */
export const deleteCookie = curry((domain, path, name) => {
  document.cookie = `${name}=; domain=${domain}; path=${path}; Max-Age=-99999999999`;
});

/* This will allow you to subscribe to changes in react-query in legacy-land.
 * This is useful for tracking changes with accountId and pageId as those are
 * managed by react-query in React-land
 */
export function createQuerySubscription(queryClient: QueryClient) {
  const queryCache = queryClient.getQueryCache();
  return function querySubscription<T = unknown>(queryKey: QueryKey | (() => QueryKey), callback: (data: T) => void) {
    let prevData;

    return queryCache.subscribe(() => {
      const extractedQueryKey = isFunction(queryKey) ? queryKey() : queryKey;

      const currData = (queryClient.getQueryData(extractedQueryKey) as T);

      if (prevData !== currData) {
        callback(currData);
        prevData = currData;
      }
    });
  };
}

